import { useRef, useState, useEffect, useCallback } from "react";
import { Link as LinkIcon, ExternalLink, Pencil, Trash2 } from "lucide-react";

const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
const A_RE = /<a\s+href="([^"]*)"(\s+target="_blank")?[^>]*>([\s\S]*?)<\/a>/gi;

// stored value (plain text + inline <a> tags, \n newlines) → editor innerHTML
function valueToHtml(value) {
  const src = String(value ?? "");
  let out = "", last = 0, m;
  const re = new RegExp(A_RE.source, "gi");
  while ((m = re.exec(src))) {
    out += esc(src.slice(last, m.index));
    out += `<a href="${esc(m[1])}"${m[2] ? ' data-newtab="1"' : ""}>${esc(m[3])}</a>`;
    last = m.index + m[0].length;
  }
  out += esc(src.slice(last));
  return out.replace(/\n/g, "<br>");
}

// editor DOM → stored value
function domToValue(node) {
  let out = "";
  node.childNodes.forEach((n) => {
    if (n.nodeType === 3) out += n.nodeValue;
    else if (n.nodeName === "BR") out += "\n";
    else if (n.nodeName === "A") {
      const nt = n.getAttribute("data-newtab") === "1" || n.getAttribute("target") === "_blank";
      out += `<a href="${n.getAttribute("href") || ""}"${nt ? ' target="_blank"' : ""}>${n.textContent}</a>`;
    } else {
      if (/^(DIV|P)$/.test(n.nodeName) && out && !out.endsWith("\n")) out += "\n";
      out += domToValue(n);
    }
  });
  return out;
}

const isValidUrl = (u) => /^(https?:\/\/|\/)/i.test((u || "").trim());

export const RichLinkEditor = ({ value, onChange, rows = 4, placeholder = "", dataTestId }) => {
  const wrapRef = useRef(null);
  const edRef = useRef(null);
  const savedRange = useRef(null);
  const editAnchor = useRef(null);
  const [toolbar, setToolbar] = useState(null);      // {top,left}
  const [popover, setPopover] = useState(null);      // {top,left}
  const [url, setUrl] = useState("");
  const [newTab, setNewTab] = useState(false);
  const [hover, setHover] = useState(null);          // {top,left,anchor}

  // hydrate editor when external value changes (not while typing)
  useEffect(() => {
    const el = edRef.current;
    if (el && document.activeElement !== el && domToValue(el) !== String(value ?? "")) {
      el.innerHTML = valueToHtml(value);
    }
  }, [value]);

  const emit = useCallback(() => { if (edRef.current) onChange(domToValue(edRef.current)); }, [onChange]);

  const relRect = (rect) => {
    const w = wrapRef.current.getBoundingClientRect();
    return { top: rect.top - w.top, left: Math.max(0, Math.min(rect.left - w.left + rect.width / 2, w.width - 40)) };
  };

  // selection → floating toolbar
  useEffect(() => {
    const onSel = () => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed || !edRef.current || !wrapRef.current ||
          !edRef.current.contains(sel.anchorNode) || !edRef.current.contains(sel.focusNode)) {
        setToolbar(null);
        return;
      }
      const rect = sel.getRangeAt(0).getBoundingClientRect();
      if (!rect.width && !rect.height) { setToolbar(null); return; }
      setToolbar(relRect(rect));
    };
    document.addEventListener("selectionchange", onSel);
    return () => document.removeEventListener("selectionchange", onSel);
  }, []);

  const openLinkPopover = (e) => {
    e.preventDefault();
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed) return;
    const range = sel.getRangeAt(0).cloneRange();
    savedRange.current = range;
    // pre-fill when the selection sits inside an existing link
    const a = (range.commonAncestorContainer.nodeType === 3
      ? range.commonAncestorContainer.parentElement
      : range.commonAncestorContainer)?.closest?.("a");
    editAnchor.current = a && edRef.current.contains(a) ? a : null;
    setUrl(editAnchor.current?.getAttribute("href") || "");
    setNewTab(editAnchor.current?.getAttribute("data-newtab") === "1");
    setPopover(relRect(range.getBoundingClientRect()));
    setToolbar(null);
  };

  const applyLink = () => {
    const href = url.trim();
    if (!isValidUrl(href)) return;
    if (editAnchor.current) {
      editAnchor.current.setAttribute("href", href);
      if (newTab) editAnchor.current.setAttribute("data-newtab", "1");
      else editAnchor.current.removeAttribute("data-newtab");
    } else if (savedRange.current) {
      const a = document.createElement("a");
      a.setAttribute("href", href);
      if (newTab) a.setAttribute("data-newtab", "1");
      const range = savedRange.current;
      try { range.surroundContents(a); }
      catch { a.appendChild(range.extractContents()); range.insertNode(a); }
      // strip nested anchors the wrap may have captured
      a.querySelectorAll("a").forEach((n) => n.replaceWith(...n.childNodes));
    }
    closePopover();
    emit();
  };

  const closePopover = () => { setPopover(null); savedRange.current = null; editAnchor.current = null; };

  // hover menu over links
  const onMouseOver = (e) => {
    const a = e.target.closest && e.target.closest("a");
    if (a && edRef.current.contains(a)) {
      const r = relRect(a.getBoundingClientRect());
      setHover({ top: r.top, left: r.left, anchor: a });
    }
  };
  const onMouseLeaveWrap = () => setHover(null);

  const hoverEdit = () => {
    const a = hover.anchor;
    editAnchor.current = a;
    savedRange.current = null;
    setUrl(a.getAttribute("href") || "");
    setNewTab(a.getAttribute("data-newtab") === "1");
    setPopover(relRect(a.getBoundingClientRect()));
    setHover(null);
  };
  const hoverOpen = () => { window.open(hover.anchor.getAttribute("href"), "_blank", "noopener"); };
  const hoverRemove = () => { hover.anchor.replaceWith(...hover.anchor.childNodes); setHover(null); emit(); };

  const onPaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
  };

  const empty = !String(value ?? "").trim();

  return (
    <div ref={wrapRef} className="relative" onMouseLeave={onMouseLeaveWrap} data-testid={dataTestId ? `${dataTestId}-wrap` : undefined}>
      <style>{`.rle-editor a{color:#0e7490;text-decoration:underline;text-underline-offset:2px;cursor:pointer}`}</style>
      {empty && (
        <div className="absolute top-2 left-3 text-sm text-muted-foreground pointer-events-none select-none">{placeholder}</div>
      )}
      <div
        ref={edRef}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        data-testid={dataTestId}
        className="rle-editor w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 overflow-y-auto whitespace-pre-wrap"
        style={{ minHeight: `${rows * 1.6 + 1}rem` }}
        onInput={emit}
        onPaste={onPaste}
        onMouseOver={onMouseOver}
      />

      {toolbar && !popover && (
        <div
          className="absolute z-30 -translate-x-1/2 -translate-y-full mb-1 flex items-center rounded-md bg-slate-900 text-white shadow-lg"
          style={{ top: toolbar.top - 6, left: toolbar.left }}
          onMouseDown={(e) => e.preventDefault()}
          data-testid={dataTestId ? `${dataTestId}-toolbar` : "rle-toolbar"}
        >
          <button type="button" className="px-2.5 py-1.5 hover:bg-slate-700 rounded-md inline-flex items-center gap-1.5 text-xs" onMouseDown={openLinkPopover} data-testid={dataTestId ? `${dataTestId}-link-btn` : "rle-link-btn"}>
            <LinkIcon size={13}/> Link
          </button>
        </div>
      )}

      {popover && (
        <div
          className="absolute z-40 -translate-x-1/2 w-72 rounded-md border border-slate-200 bg-white shadow-xl p-3 space-y-2"
          style={{ top: popover.top + 22, left: Math.max(150, popover.left) }}
          data-testid={dataTestId ? `${dataTestId}-link-popover` : "rle-link-popover"}
        >
          <label className="text-[11px] font-medium text-slate-500">Paste URL</label>
          <input
            autoFocus
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") applyLink(); if (e.key === "Escape") closePopover(); }}
            placeholder="https://example.com or /pricing"
            className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
            data-testid="rle-url-input"
          />
          {!isValidUrl(url) && url.trim() !== "" && (
            <div className="text-[10px] text-rose-600">Use https://… or an internal path like /contact</div>
          )}
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
            <input type="checkbox" checked={newTab} onChange={(e) => setNewTab(e.target.checked)} data-testid="rle-newtab-checkbox"/>
            Open link in new tab
          </label>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" className="text-xs px-2.5 py-1 rounded border border-slate-300 hover:bg-slate-50" onClick={closePopover} data-testid="rle-cancel-btn">Cancel</button>
            <button type="button" disabled={!isValidUrl(url)} className="text-xs px-2.5 py-1 rounded bg-cyan-600 text-white hover:bg-cyan-700 disabled:opacity-50" onClick={applyLink} data-testid="rle-apply-btn">Apply</button>
          </div>
        </div>
      )}

      {hover && !popover && !toolbar && (
        <div
          className="absolute z-30 -translate-x-1/2 -translate-y-full flex items-center rounded-md bg-slate-900 text-white shadow-lg text-[11px]"
          style={{ top: hover.top - 4, left: hover.left }}
          onMouseDown={(e) => e.preventDefault()}
          data-testid="rle-hover-menu"
        >
          <button type="button" className="px-2 py-1.5 hover:bg-slate-700 rounded-l-md inline-flex items-center gap-1" onClick={hoverEdit} data-testid="rle-hover-edit"><Pencil size={11}/> Edit</button>
          <button type="button" className="px-2 py-1.5 hover:bg-slate-700 inline-flex items-center gap-1" onClick={hoverOpen} data-testid="rle-hover-open"><ExternalLink size={11}/> Open</button>
          <button type="button" className="px-2 py-1.5 hover:bg-slate-700 rounded-r-md inline-flex items-center gap-1" onClick={hoverRemove} data-testid="rle-hover-remove"><Trash2 size={11}/> Remove</button>
        </div>
      )}
    </div>
  );
};

// Renders stored text-with-anchors as React nodes (for previews)
export const RichText = ({ text }) => {
  const src = String(text || "");
  const re = new RegExp(A_RE.source, "gi");
  const out = [];
  let last = 0, m, i = 0;
  while ((m = re.exec(src))) {
    if (m.index > last) out.push(src.slice(last, m.index));
    out.push(
      <a key={i++} href={m[1]} target={m[2] ? "_blank" : undefined} rel="noopener noreferrer" className="underline text-cyan-700">{m[3]}</a>
    );
    last = m.index + m[0].length;
  }
  out.push(src.slice(last));
  return out;
};

export default RichLinkEditor;
